import React, { useCallback, useEffect } from 'react';

import config from 'config';
import styled, { css } from 'styled-components';

import { PromoMessage } from '~/shared/components/PromoMessage';
import featureNames from '~/shared/constants/featureFlags';
import { getPromoPricingActiveAsset } from '~/shared/utils/getPromoPricingActiveAsset';
import { useMysteryBoxSelectionsContext } from '~/shared/utils/mysteryBoxSelections';
import { getPromoPricingUIAssetContainerName } from '~/shared/utils/promoPricingUI';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useAsset } from '~/techstyle-shared/react-assets';
import {
  Button,
  useProductContext,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import { useSession } from '~/techstyle-shared/redux-core';

import { PromoPricingMessage } from '../PromoPricingMessage';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';
import VIPOptInMessage from './VIPOptInMessage';

const Wrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.vipCtaWrapper}
  ${({ $isSingleCtaVipOptInEnabled }) =>
    $isSingleCtaVipOptInEnabled &&
    css`
      padding: 0;
    `}
  ${({ $hideBorder }) =>
    $hideBorder &&
    css`
      background: none;
      border: none;
    `}
`;

const PromoMessageWrapper = styled.div`
  ${({ theme }) => theme.pdpStyles.vipOptIn.promoMessageWrapper}
`;

const VIPAddToBagButton = styled(Button).attrs({
  variant: 'primaryAddToBag',
  size: 'lg',
})`
  ${({ theme }) => theme.pdpStyles.vipAddToBagButton}
`;

const promoPriceStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const promoMessageStyle = css`
  ${({ theme }) => theme.pdpStyles.promoMessage.copy};
`;

const priceStyle = css`
  font-weight: 700;
`;

const labelStyle = css`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: ${-0.4 / 18}em;
`;

const InStockAtbButtonCopy = () => {
  const { enabled: isSingleCtaVipOptInEnabled } = useHasFeature(
    featureNames.SINGLE_CTA_VIP_OPT_IN
  );
  return (
    <>
      {isSingleCtaVipOptInEnabled ? (
        <FormattedMessage
          id="vip_opt_in.add_to_bag"
          defaultMessage="Add to Bag"
        />
      ) : (
        <FormattedMessage
          id="global_cta.add_to_bag_vip"
          defaultMessage="VIP Add to Bag"
        />
      )}
    </>
  );
};

function VIPOptIn({ ...rest }) {
  const { isLoggedIn } = useSession();
  const { isVip } = useMembership();
  const intl = useIntl();

  const {
    addToCart,
    addMysteryBoxToCart,
    handleSharedValidation,
    isForceDisableCtaEnabled,
    isLoading,
    isOutOfStock,
    signupAsVIPCart,
    setErrorMessage,
    hasError,
    isAssortmentTestProductDisabled,
    showVipOptInMessage,
    onSuccessfulAddToCart,
  } = useProductDetailCTAContext();
  const productDetailFromCtx = useProductDetailContext();

  const disabled = isLoading || isOutOfStock || isForceDisableCtaEnabled;

  const isMysteryBox = productDetailFromCtx.product?.isMysteryBundle;
  const { isSizeComplete, hasSoldOutSizeSelected } =
    useMysteryBoxSelectionsContext();
  const mysteryBoxCTADisabled =
    isLoading || !isSizeComplete || hasSoldOutSizeSelected;

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);

  const handleCTAClick = useCallback(async () => {
    try {
      if (!isLoggedIn) {
        // storing in the context to remember to add to VIP cart after signup
        await signupAsVIPCart();
        await handleSharedValidation();
      } else {
        await handleSharedValidation();
        if (isMysteryBox) {
          await addMysteryBoxToCart({ enableVIPCart: true });
        } else {
          await addToCart({ enableVIPCart: true });
        }

        onSuccessfulAddToCart && onSuccessfulAddToCart();
      }
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    addToCart,
    handleSharedValidation,
    intl,
    isLoggedIn,
    setErrorMessage,
    signupAsVIPCart,
    onSuccessfulAddToCart,
    isMysteryBox,
    addMysteryBoxToCart,
  ]);

  const product = useProductContext();
  const promoPricingConfig = config.get('public.brand.promoPricingUI');

  const { enabled: isPrioritizedFeatureEnabled } = useHasFeature(
    featureNames.PRIORITIZED_PROMOS
  );
  const isPrioritizedEnabled =
    isPrioritizedFeatureEnabled && promoPricingConfig?.showPromoPricingOffer;

  const { enabled: isSingleCtaVipOptInEnabled } = useHasFeature(
    featureNames.SINGLE_CTA_VIP_OPT_IN
  );

  const offerLabelContainerName = getPromoPricingUIAssetContainerName(
    promoPricingConfig,
    isVip
  );
  const assetContainerOfferLabel = useAsset(offerLabelContainerName);

  const promoPricingActiveAsset = getPromoPricingActiveAsset({
    isPrioritizedEnabled,
    assetContainerOfferLabel,
    product,
  });

  const isPromoPricingActive = Boolean(promoPricingActiveAsset);
  const pdpLabel = promoPricingActiveAsset?.options?.customVars?.pdpLabel;
  const htmlText = promoPricingActiveAsset?.options?.htmlText;

  if (isAssortmentTestProductDisabled) {
    return null;
  }

  return (
    <Wrapper
      $isSingleCtaVipOptInEnabled={isSingleCtaVipOptInEnabled}
      $hideBorder={!showVipOptInMessage || isSingleCtaVipOptInEnabled}
      {...rest}
    >
      {!isSingleCtaVipOptInEnabled && (
        <PromoMessageWrapper>
          {isPromoPricingActive ? (
            <PromoPricingMessage
              promoPriceStyle={promoPriceStyle}
              pdpLabel={pdpLabel}
              isPromoPricingActive={isPromoPricingActive}
              priceStyle={priceStyle}
              htmlText={htmlText}
              product={product}
            />
          ) : (
            <PromoMessage
              showPromoPrice
              promoPriceStyle={promoPriceStyle}
              promoMessageStyle={promoMessageStyle}
            />
          )}
        </PromoMessageWrapper>
      )}
      <VIPAddToBagButton
        variant="primaryAddToBag"
        labelStyle={labelStyle}
        onClick={handleCTAClick}
        data-autotag="pdp-vip-optin-add-to-bag"
        disabled={isMysteryBox ? mysteryBoxCTADisabled : disabled}
        data-cnstrc-btn="add-to-cart"
      >
        {isOutOfStock ? (
          <FormattedMessage
            id="global_cta.out_of_stock"
            defaultMessage="Out of Stock"
          />
        ) : (
          <InStockAtbButtonCopy />
        )}
      </VIPAddToBagButton>{' '}
      {showVipOptInMessage && !isSingleCtaVipOptInEnabled && (
        <VIPOptInMessage />
      )}
    </Wrapper>
  );
}

export default VIPOptIn;
